import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { Upload, FileText, ShieldCheck, Key, Loader2, AlertTriangle, Download } from "lucide-react";

const BACKEND_BASE = (process.env.REACT_APP_BACKEND_URL || "http://localhost:8000").replace(/\/+$/, "");
const API = `${BACKEND_BASE}/api`;
const AGENT_URL = "http://localhost:7979";

export default function VisibleSignPage() {
  const [file, setFile] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [docId, setDocId] = useState(null);
  const [selectedCert, setSelectedCert] = useState(null);
  const [certificates, setCertificates] = useState([]);
  const [pin, setPin] = useState("");
  const [placing, setPlacing] = useState({ x_pct: 0.62, y_pct: 0.80, w_pct: 0.28, h_pct: 0.12 });
  const [placingEnabled, setPlacingEnabled] = useState(false);
  const [agentOnline, setAgentOnline] = useState(false);
  const [signing, setSigning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const overlayRef = useRef();
  const dragRef = useRef(null);

  useEffect(() => {
    return () => {
      if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    };
  }, [pdfUrl]);

  useEffect(() => {
    const run = async () => {
      try {
        const statusRes = await fetch(`${AGENT_URL}/status`, { signal: AbortSignal.timeout(2500) });
        const status = await statusRes.json();
        if (!status) return;
        const certRes = await fetch(`${AGENT_URL}/certificates`, { signal: AbortSignal.timeout(3000) });
        const certData = await certRes.json();
        const certs = certData.certificates || [];
        setCertificates(certs);
        setSelectedCert(certs[0]?.alias || null);
        setAgentOnline(true);
      } catch {
        setAgentOnline(false);
      }
    };
    run();
  }, []);

  const markerStyle = useMemo(() => ({
    left: `${placing.x_pct * 100}%`,
    top: `${placing.y_pct * 100}%`,
    width: `${placing.w_pct * 100}%`,
    height: `${placing.h_pct * 100}%`,
  }), [placing]);

  const onPickFile = (f) => {
    if (!f) return;
    setError(null);
    setResult(null);
    if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    setFile(f);
    setPdfUrl(URL.createObjectURL(f));
  };

  const onOverlayClick = (e) => {
    if (!placingEnabled) return;
    if (dragRef.current) return;
    const rect = overlayRef.current?.getBoundingClientRect();
    if (!rect) return;
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    setPlacing((prev) => ({ ...prev, x_pct: Math.max(0.02, Math.min(0.95, x)), y_pct: Math.max(0.02, Math.min(0.95, y)) }));
  };

  const clamp = (v, min, max) => Math.max(min, Math.min(max, v));

  const beginDrag = (mode) => (e) => {
    if (!placingEnabled) return;
    e.preventDefault();
    e.stopPropagation();
    const rect = overlayRef.current?.getBoundingClientRect();
    if (!rect) return;
    dragRef.current = {
      mode,
      startX: e.clientX,
      startY: e.clientY,
      start: { ...placing },
      rect,
    };
    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", endDrag);
  };

  const onPointerMove = (e) => {
    const drag = dragRef.current;
    if (!drag) return;
    const dx = (e.clientX - drag.startX) / drag.rect.width;
    const dy = (e.clientY - drag.startY) / drag.rect.height;
    const s = drag.start;

    if (drag.mode === "move") {
      const x_pct = clamp(s.x_pct + dx, 0.0, 1.0 - s.w_pct);
      const y_pct = clamp(s.y_pct + dy, 0.0, 1.0 - s.h_pct);
      setPlacing((prev) => ({ ...prev, x_pct, y_pct }));
      return;
    }

    const minW = 0.12;
    const minH = 0.07;
    let x = s.x_pct;
    let y = s.y_pct;
    let w = s.w_pct;
    let h = s.h_pct;

    if (drag.mode.includes("e")) w = clamp(s.w_pct + dx, minW, 0.9);
    if (drag.mode.includes("s")) h = clamp(s.h_pct + dy, minH, 0.6);
    if (drag.mode.includes("w")) {
      const nx = clamp(s.x_pct + dx, 0.0, s.x_pct + s.w_pct - minW);
      w = clamp(s.w_pct - (nx - s.x_pct), minW, 0.9);
      x = nx;
    }
    if (drag.mode.includes("n")) {
      const ny = clamp(s.y_pct + dy, 0.0, s.y_pct + s.h_pct - minH);
      h = clamp(s.h_pct - (ny - s.y_pct), minH, 0.6);
      y = ny;
    }

    if (x + w > 1.0) w = 1.0 - x;
    if (y + h > 1.0) h = 1.0 - y;

    setPlacing((prev) => ({ ...prev, x_pct: x, y_pct: y, w_pct: w, h_pct: h }));
  };

  const endDrag = () => {
    dragRef.current = null;
    window.removeEventListener("pointermove", onPointerMove);
    window.removeEventListener("pointerup", endDrag);
  };

  const handleSignVisible = async () => {
    if (!file) return setError("Selecione um PDF.");
    if (!agentOnline) return setError("Agente local offline. Inicie o agente na porta 7979.");
    const certObj = certificates.find((c) => c.alias === selectedCert);
    if (!certObj?.cert_pem) return setError("Certificado inválido/sem PEM.");

    setSigning(true);
    setError(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("sign_type", "PAdES");
      const up = await axios.post(`${API}/documents/upload`, fd);
      const id = up.data.document_id;
      setDocId(id);

      const prep = await axios.post(`${API}/sign/prepare/${id}`, {
        certificate_pem: certObj.cert_pem,
        visible_signature: true,
        ...placing,
      });

      const agentRes = await axios.post(`${AGENT_URL}/sign`, {
        hash: prep.data.hash,
        certificate_alias: selectedCert,
        pin,
        mode: "birdid",
      }, { timeout: 35000 });

      if (agentRes.data?.error) throw new Error(agentRes.data.error);

      const done = await axios.post(`${API}/sign/complete`, {
        document_id: id,
        signature_base64: agentRes.data.signature,
        certificate_pem: agentRes.data.certificate_pem || certObj.cert_pem,
        sign_type: "PAdES",
      });

      setResult(done.data);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setSigning(false);
    }
  };

  const handleDownload = async () => {
    if (!docId) return;
    try {
      const res = await axios.get(`${API}/documents/${docId}/download/signed`);
      const { filename, content_base64 } = res.data;
      const bytes = atob(content_base64);
      const arr = new Uint8Array(bytes.length);
      for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
      const blob = new Blob([arr], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    }
  };

  return (
    <div className="p-8 max-w-6xl mx-auto space-y-5">
      <div>
        <h1 className="text-3xl font-bold text-[#1A1A1A]" style={{ fontFamily: "Outfit, sans-serif" }}>Assinatura Visível (PDF)</h1>
        <p className="text-gray-500 text-sm">Nova variação: clique no documento para posicionar o campo de assinatura digital visível.</p>
      </div>

      {error && (
        <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
          <AlertTriangle size={15} className="mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      <div className="bg-white border border-black/10 rounded-xl p-4 space-y-4">
        <div className="flex gap-3 items-center">
          <label className="px-3 py-2 rounded-md border border-gray-200 text-sm cursor-pointer hover:bg-gray-50">
            <Upload size={14} className="inline mr-2" />Selecionar PDF
            <input type="file" accept="application/pdf,.pdf" className="hidden" onChange={(e) => onPickFile(e.target.files?.[0])} />
          </label>
          <select
            value={selectedCert || ""}
            onChange={(e) => setSelectedCert(e.target.value)}
            className="border border-gray-200 rounded-md px-3 py-2 text-sm"
          >
            {certificates.map((c) => <option key={c.alias} value={c.alias}>{c.cn || c.alias}</option>)}
          </select>
          <input
            type="password"
            value={pin}
            onChange={(e) => setPin(e.target.value)}
            placeholder="OTP/PIN"
            className="border border-gray-200 rounded-md px-3 py-2 text-sm"
          />
          <button
            onClick={() => setPlacingEnabled((v) => !v)}
            className={`px-3 py-2 rounded-md text-sm ${placingEnabled ? "bg-[#163A24] text-white" : "border border-gray-200"}`}
          >
            {placingEnabled ? "Posicionamento Ativo" : "Ativar Posicionamento"}
          </button>
          <select
            className="border border-gray-200 rounded-md px-3 py-2 text-sm"
            value={`${placing.w_pct.toFixed(2)}:${placing.h_pct.toFixed(2)}`}
            onChange={(e) => {
              const [w, h] = e.target.value.split(":").map(Number);
              setPlacing((prev) => ({ ...prev, w_pct: w, h_pct: h }));
            }}
          >
            <option value="0.24:0.10">Pequeno</option>
            <option value="0.28:0.12">Médio</option>
            <option value="0.34:0.14">Grande</option>
          </select>
          <button
            onClick={handleSignVisible}
            disabled={!file || signing}
            className="ml-auto px-4 py-2 rounded-md bg-[#163A24] text-white text-sm disabled:opacity-50"
          >
            {signing ? <><Loader2 size={14} className="inline mr-2 animate-spin" />Assinando...</> : <><Key size={14} className="inline mr-2" />Assinar com Campo Visível</>}
          </button>
        </div>

        {file && <p className="text-xs text-gray-500"><FileText size={12} className="inline mr-1" />{file.name}</p>}
        {placingEnabled && (
          <p className="text-xs text-[#163A24] bg-[#163A24]/5 border border-[#163A24]/20 rounded-md px-2 py-1">
            Clique para posicionar. Arraste a caixa para mover e use os cantos para redimensionar.
          </p>
        )}

        <div className="relative w-full h-[85vh] rounded-lg overflow-hidden border border-gray-200 bg-gray-50">
          {pdfUrl ? (
            <>
              <iframe title="pdf-preview" src={`${pdfUrl}#zoom=page-fit&view=FitH`} className="w-full h-full" />
              <div
                ref={overlayRef}
                onClick={onOverlayClick}
                className={`absolute inset-0 ${placingEnabled ? "cursor-crosshair pointer-events-auto" : "pointer-events-none"}`}
                style={{ background: placingEnabled ? "rgba(22,58,36,0.03)" : "transparent" }}
              >
                <div
                  className={`absolute border-2 ${placingEnabled ? "border-[#163A24] shadow-lg" : "border-[#163A24]/60"} bg-[#8FBC8F]/20`}
                  style={markerStyle}
                >
                  <div
                    className="h-full w-full"
                    onPointerDown={beginDrag("move")}
                    style={{ cursor: placingEnabled ? "move" : "default" }}
                  >
                    <div className="text-[10px] bg-[#163A24] text-white px-1 py-0.5 inline-block">Assinatura Digital</div>
                  </div>
                  {placingEnabled && (
                    <>
                      <div onPointerDown={beginDrag("nw")} className="absolute -left-1.5 -top-1.5 w-3 h-3 rounded-full bg-[#163A24] cursor-nwse-resize" />
                      <div onPointerDown={beginDrag("ne")} className="absolute -right-1.5 -top-1.5 w-3 h-3 rounded-full bg-[#163A24] cursor-nesw-resize" />
                      <div onPointerDown={beginDrag("sw")} className="absolute -left-1.5 -bottom-1.5 w-3 h-3 rounded-full bg-[#163A24] cursor-nesw-resize" />
                      <div onPointerDown={beginDrag("se")} className="absolute -right-1.5 -bottom-1.5 w-3 h-3 rounded-full bg-[#163A24] cursor-nwse-resize" />
                    </>
                  )}
                </div>
              </div>
            </>
          ) : (
            <div className="h-full flex items-center justify-center text-sm text-gray-400">
              <ShieldCheck size={16} className="mr-2" />Selecione um PDF para pré-visualizar
            </div>
          )}
        </div>

        {result && (
          <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded-lg p-3">
            <span className="text-sm text-green-700">Assinado com sucesso: {result.signed_filename}</span>
            <button onClick={handleDownload} className="px-3 py-2 rounded-md bg-[#163A24] text-white text-sm">
              <Download size={14} className="inline mr-1" />Baixar
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
